/**
 * Phase 3.43 — Admin-Übersicht: Service Provider Freigabe-Workflow.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Handshake, ShieldCheck, ShieldAlert, Filter, Loader2, ArrowLeft, CheckCircle2, XCircle, Globe, Phone, Mail } from "lucide-react";
import { toast } from "sonner";

export default function AdminServiceProvidersPage() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState(null);
    const [branches, setBranches] = useState([]);
    const [filter, setFilter] = useState({ verified: "", branch: "" });
    const [savingId, setSavingId] = useState(null);

    useEffect(() => {
        if (loading) return;
        if (!user || user.role !== "admin") {
            navigate("/");
        }
    }, [user, loading, navigate]);

    const load = async () => {
        try {
            const params = new URLSearchParams();
            if (filter.verified !== "") params.set("verified", filter.verified);
            if (filter.branch) params.set("branch", filter.branch);
            const r = await api.get(`/admin/service-providers${params.toString() ? `?${params}` : ""}`);
            setItems(r.data.items || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fehler beim Laden");
        }
    };
    useEffect(() => { load(); }, [filter.verified, filter.branch]);
    useEffect(() => {
        api.get("/service-providers/branches")
            .then((r) => setBranches(r.data.items || []))
            .catch(() => setBranches([]));
    }, []);

    const setVerified = async (id, value) => {
        setSavingId(id);
        try {
            await api.patch(`/admin/service-providers/${id}`, { verified: value });
            toast.success(value ? "Freigegeben" : "Freigabe entzogen");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fehler");
        } finally {
            setSavingId(null);
        }
    };
    const setActive = async (id, value) => {
        setSavingId(id);
        try {
            await api.patch(`/admin/service-providers/${id}`, { active: value });
            toast.success(value ? "Aktiviert" : "Deaktiviert");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fehler");
        } finally {
            setSavingId(null);
        }
    };

    const branchLabel = (k) => branches.find((b) => b.key === k)?.label || k;

    if (items === null) {
        return <div className="max-w-6xl mx-auto p-12 flex items-center gap-3 text-gray-600"><Loader2 className="w-5 h-5 animate-spin" /> Lade Dienstleister…</div>;
    }

    return (
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-8" data-testid="admin-sp-page">
            <Link to="/admin" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-600 hover:text-[#111827] mb-3">
                <ArrowLeft className="w-3.5 h-3.5" /> Admin-Übersicht
            </Link>
            <div className="mb-6">
                <div className="text-xs font-bold uppercase tracking-wider text-[#FFCC00] flex items-center gap-2 mb-1">
                    <Handshake className="w-3.5 h-3.5" /> Dienstleister-Verwaltung
                </div>
                <h1 className="font-display text-3xl md:text-4xl font-black">Service Provider</h1>
                <p className="text-gray-600 text-sm mt-1">Freigabe-Workflow für selbst registrierte und System-Dienstleister.</p>
            </div>

            <div className="mb-4 flex items-center gap-2 flex-wrap">
                <Filter className="w-4 h-4 text-gray-500" />
                <button onClick={() => setFilter({ ...filter, verified: "" })} className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider border ${filter.verified === "" ? "bg-[#111827] text-white border-[#111827]" : "bg-white border-gray-300 text-gray-700"}`} data-testid="filter-all">
                    Alle
                </button>
                <button onClick={() => setFilter({ ...filter, verified: "false" })} className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider border ${filter.verified === "false" ? "bg-[#111827] text-white border-[#111827]" : "bg-white border-gray-300 text-gray-700"}`} data-testid="filter-pending">
                    Wartet auf Freigabe
                </button>
                <button onClick={() => setFilter({ ...filter, verified: "true" })} className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider border ${filter.verified === "true" ? "bg-[#111827] text-white border-[#111827]" : "bg-white border-gray-300 text-gray-700"}`} data-testid="filter-approved">
                    Freigegeben
                </button>
                <select
                    value={filter.branch}
                    onChange={(e) => setFilter({ ...filter, branch: e.target.value })}
                    className="ml-auto rounded-sm border border-gray-300 px-2 py-1.5 text-xs bg-white"
                    data-testid="filter-branch"
                >
                    <option value="">Alle Branchen</option>
                    {branches.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
                </select>
            </div>

            {items.length === 0 ? (
                <div className="bg-white border border-dashed border-gray-300 rounded-sm p-12 text-center text-gray-500" data-testid="admin-sp-empty">
                    Keine Einträge gefunden.
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map((sp) => (
                        <div key={sp.id} className="bg-white border border-gray-200 rounded-sm p-4" data-testid={`sp-${sp.id}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <h3 className="font-bold text-base text-[#111827]" data-testid={`sp-${sp.id}-name`}>{sp.name}</h3>
                                        {sp.branch && <Badge className="bg-gray-100 text-gray-700">{branchLabel(sp.branch)}</Badge>}
                                        {sp.verified ? (
                                            <Badge className="bg-emerald-100 text-emerald-800 gap-1" data-testid={`sp-${sp.id}-verified`}>
                                                <ShieldCheck className="w-3 h-3" /> Freigegeben
                                            </Badge>
                                        ) : (
                                            <Badge className="bg-amber-100 text-amber-800 gap-1">
                                                <ShieldAlert className="w-3 h-3" /> Wartet
                                            </Badge>
                                        )}
                                        {sp.is_self_registered && <Badge className="bg-blue-100 text-blue-800">Self-Reg</Badge>}
                                        {sp.owner_dealer_id && <Badge className="bg-orange-100 text-orange-800">Dealer-Eigen</Badge>}
                                        {!sp.active && <Badge className="bg-red-100 text-red-800">Deaktiviert</Badge>}
                                    </div>
                                    {sp.description && <div className="text-xs text-gray-600 mt-1">{sp.description}</div>}
                                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-700">
                                        {sp.contact_email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{sp.contact_email}</span>}
                                        {sp.contact_phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{sp.contact_phone}</span>}
                                        {sp.website && <a href={sp.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline text-[#111827] font-bold"><Globe className="w-3 h-3" />Website</a>}
                                    </div>
                                    <div className="flex flex-wrap gap-1 mt-2">
                                        {(sp.regions || []).slice(0, 8).map((r) => (
                                            <span key={r} className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded-sm text-[10px] font-mono-num">{r}</span>
                                        ))}
                                    </div>
                                    <div className="text-[10px] text-gray-400 mt-2">Erstellt {(sp.created_at || "").slice(0, 16).replace("T", " ")}</div>
                                </div>
                                <div className="flex flex-col gap-2 shrink-0">
                                    {sp.verified ? (
                                        <Button
                                            onClick={() => setVerified(sp.id, false)}
                                            disabled={savingId === sp.id}
                                            variant="outline"
                                            className="font-bold rounded-sm uppercase tracking-wider text-xs"
                                            data-testid={`sp-${sp.id}-revoke`}
                                        >
                                            {savingId === sp.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3.5 h-3.5 mr-1" />}
                                            Freigabe entziehen
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={() => setVerified(sp.id, true)}
                                            disabled={savingId === sp.id}
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-sm uppercase tracking-wider text-xs"
                                            data-testid={`sp-${sp.id}-approve`}
                                        >
                                            {savingId === sp.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                                            Freigeben
                                        </Button>
                                    )}
                                    <Button
                                        onClick={() => setActive(sp.id, !sp.active)}
                                        disabled={savingId === sp.id}
                                        variant="outline"
                                        className="font-bold rounded-sm uppercase tracking-wider text-xs"
                                        data-testid={`sp-${sp.id}-toggle-active`}
                                    >
                                        {sp.active ? "Deaktivieren" : "Aktivieren"}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
